import { createContext, useContext, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { getIdsQuery, getUid } from 'Services/WeaverUtils'
import { useSpreadSheetData } from './SpreadSheetDataProvider'
import ShortcutManager from 'Services/ShortcutManager'
import { useHotKeys } from 'Hooks'
import { setTableConfig } from 'Actions/explore'
import {
  setColumns,
  setColWidths,
  setIsEscapingEditMode,
  setIsFetching,
  setIsInEditMode,
  setLoading,
  setNodeOrder,
  setRelationsMap,
  setCount
} from './SpreadSheetDataProvider/actions'
import { useTableData } from './TableDataProvider'
import {
  setTableData
} from './TableDataProvider/actions'
import {
  defaultColConf,
  loadRelationTargets,
  recalculateColumnWidths
} from './helpers'

SpreadSheetHelpersContext = createContext()
headerColumnWidth = 50
resultPageSize = 30

SpreadSheetHelpers = ({ children, setTableConfig, tableProps, width, title, ids }) ->

  [{
    columns,
    columnWidthMultiplier,
    columnWidths,
    edits,
    filters,
    isInEditMode,
    loading,
    nodeOrder,
    relationsMap,
    resizedColumnWidths,
    sorter
  }, dispatch] = useSpreadSheetData()

  [tableData, dispatchTableData] = useTableData()

  [columnWidthMultiplier, setColumnWidthMultiplier] = useState(1)
  [columnCount, setColumnCount] = useState(0)
  [isModalVisible, setIsModalVisible] = useState(false)
  [isIdColumnVisible, setIsIdColumnVisible] = useState(false)

  getSorterConfig = (sorter) ->
    if !sorter?.key
      {
        clause: ''
        binding: ''
      }
    else
      direction = sorter.ascending && 'ASC' || 'DESC'
      {
        clause: "ORDER BY #{ direction }(?sort) ."
        binding: "_ ?node <#{ sorter.key }> ?sort ."
      }

  useEffect( ->
    return unless (sorter? or filters?)
    ( ->
      if ids
        query = getIdsQuery(ids, getSorterConfig(sorter), filters)
      else
        query = tableProps.data.query(getSorterConfig(sorter), filters)
      res = await query.find()
      { node } = res
      ids = _.uniq(node.map(getUid))
      dispatch(setNodeOrder(ids))
      dispatch(setCount(ids.length))
    )()
    undefined
  , [filters, sorter, tableProps])

  useEffect( ->
    setColumnCount(columns.length)
    undefined
  , [columns])

  useEffect( ->
    return unless nodeOrder.length > 0
    ( ->
      dispatch(setLoading(true))
      await fetchNodes(0, resultPageSize)
      dispatch(setLoading(false))
    )()
    undefined
  , [isIdColumnVisible, nodeOrder])

  useEffect( ->
    if isInEditMode
      ShortcutManager.pause(['esc', 'tab', 'enter'])
    else
      ShortcutManager.unpause()
    undefined
  , [isInEditMode])

  useHotKeys([{
    sequence: 'esc'
    fn: ->
      if isInEditMode
        dispatch(setIsEscapingEditMode(true))
        dispatch(setIsInEditMode(false))
    temporary: true
    watchers: [isInEditMode, edits]
  }])

  fetchNodes = (start, end) ->

    ids = nodeOrder.slice(start, end + 1)
      .filter((id) -> id? && !tableData[id]) # filter already loaded
      .map((id) -> id.split('@')[0])

    if ids.length > 0 then dispatch(setIsFetching(true))

    query = new Weaver.Query()
      .restrict(ids)
      .selectOut('*')
      .withRelations()

    try
      applySelects = tableProps.selects ?= (q) -> q
      newNodes = await applySelects(query).find()
    catch e
      console.log(e)

    allNodes = _.values(tableData)
      .concat(newNodes)
      .filter((n) -> n?)

    newColumns = getColumnConfig(allNodes)

    if newColumns.length isnt columns.length
      dispatch(setColumns(newColumns))

    newRelationsMap = await loadRelationTargets({
      nodes: newNodes,
      columns: newColumns.filter((c) -> c.relationConf? ),
      relationsMap
    })

    onNodesLoaded(newNodes, newColumns, newRelationsMap)
    dispatch(setIsFetching(false))

  # Load subsequent page of nodes
  loadMore = (start, finish) ->
    return if loading
    await fetchNodes(start, finish)

  onNodesLoaded = (newNodes, newColumns, newRelationsMap) ->
    { newTableData, newColWidths } = buildTableData({
      nodes: newNodes,
      columns: newColumns,
      columnWidths,
      relationsMap: newRelationsMap
    })

    dispatch(setRelationsMap({ relationsMap..., newRelationsMap... }))
    dispatchTableData(setTableData(newTableData))

    if newColWidths
      dispatch(setColWidths(newColWidths))
    dispatch(setLoading(false))

  getColumnConfig = (rows) ->
    tableProps.data.columns or defaultColConf(rows, isIdColumnVisible)

  buildTableData = ({ nodes, columns, relationsMap }) ->
    newTableData = nodes.reduce((acc, n) ->
      { acc..., [getUid(n)] : n }
    , {})

    currentWidths = recalculateColumnWidths({
      columns
      tableData: newTableData,
      relationsMap,
      width
    })
    mergedWidths = _.values(currentWidths).reduce((acc, c, i) ->
      acc[i] = Math.max(c, columnWidths[i] or 0)
      acc
    , {})
    newColWidths = undefined
    unless _.isEqual(columnWidths, mergedWidths)
      newColWidths = mergedWidths
    { newTableData, newColWidths }

  getColumnWidth = ({ index }) ->
    if index is 0
      headerColumnWidth
    else
      getBaseColumnWidth(index) * columnWidthMultiplier

  getBaseColumnWidth = (index) ->
    if index is 0
      headerColumnWidth
    else
      resizedColumnWidths[index - 1] or
      columnWidths[index - 1] or 0

  getTableWidth = ->
    [0..columns.length - 1].reduce((acc, index) ->
      acc + getBaseColumnWidth(index)
    , 0)

  <SpreadSheetHelpersContext.Provider
    value={{
      columnCount,
      columnWidthMultiplier,
      getColumnWidth,
      getTableWidth,
      headerColumnWidth,
      isModalVisible,
      isIdColumnVisible,
      loadMore,
      resultPageSize,
      setColumnWidthMultiplier,
      setIsIdColumnVisible,
      setIsModalVisible
    }}
  >
    { children }
  </SpreadSheetHelpersContext.Provider>

export useHelpers = () ->
  useContext(SpreadSheetHelpersContext)

export SpreadSheetHelpersProvider = connect(
  null,
  { setTableConfig }
)(SpreadSheetHelpers)
